// Το API των προκαταβολών ταμείου (backend routes/counterDeposits.js), μαζί με τον διακόπτη του
// καταστήματος (/deposits/settings). Η χρέωση κάρτας/IRIS κρατά το αίτημα ανοιχτό όσο περιμένει
// το τερματικό (≤ ~3′): ό,τι δεν φέρνει σαφή απάντηση (δίκτυο, 5xx, timeout) είναι «αβέβαιο» —
// ΠΟΤΕ «απέτυχε», γιατί μπορεί να χρεώθηκε.

import { API_BASE_URL } from '../config/api';
import type {
  CreateDepositBody, CreateDepositResponse, CustomerDeposit, DepositView, DepositsConfig, DepositsSettingsBody,
  PosTerminalLite, RefundBody, RefundResponse, RefundView,
} from './types';

export class DepositApiError extends Error {
  status: number;
  code: string | null;
  body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>) {
    super(String(body.error || body.message || `HTTP ${status}`));
    this.status = status;
    this.code = typeof body.code === 'string' ? body.code : null;
    this.body = body;
  }
}

/** Πόσο περιμένουμε την απάντηση μιας χρέωσης (ο server περιμένει το τερματικό ~3′). */
export const CHARGE_TIMEOUT_MS = 240000;

type Raw = { status: number; ok: boolean; data: Record<string, unknown> };

async function send(path: string, init: { method?: string; body?: unknown; timeoutMs?: number } = {}): Promise<Raw> {
  const ctrl = typeof AbortController !== 'undefined' && init.timeoutMs ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), init.timeoutMs) : null;
  try {
    const res = await fetch(`${API_BASE_URL}/api${path}`, {
      method: init.method || 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('xentrix_token')}`,
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, ok: res.ok, data: (data && typeof data === 'object' ? data : {}) as Record<string, unknown> };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const r = await send(path, init);
  if (!r.ok) throw new DepositApiError(r.status, r.data);
  return r.data as unknown as T;
}

/**
 * Αποτέλεσμα μιας χρέωσης/επιστροφής:
 *  - ok:        εγκρίθηκε (ή μετρητά καταχωρήθηκαν)
 *  - declined:  απορρίφθηκε στο τερματικό — καμία χρέωση, νέα προσπάθεια = νέο κλειδί
 *  - failed:    σίγουρα καμία χρέωση (π.χ. δεν πήραμε υπογραφή) — νέα προσπάθεια = νέο κλειδί
 *  - uncertain: δεν ξέρουμε αν χρεώθηκε (το τερματικό/δίκτυο δεν απάντησε) — ΙΔΙΟ κλειδί, ποτέ αυτόματα
 *  - error:     ο server αρνήθηκε πριν από κάθε χρέωση (4xx) — το μήνυμά του στον ταμία
 */
export type ChargeOutcome<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'declined'; message: string | null; data: Partial<T> }
  | { kind: 'failed'; message: string | null; code: string | null }
  | { kind: 'uncertain'; message: string | null; code: string | null; id: number | null }
  | { kind: 'error'; status: number; message: string | null; code: string | null };

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);

export function toOutcome<T>(r: Raw): ChargeOutcome<T> {
  const d = r.data;
  const code = str(d.code);
  const message = str(d.message) || str(d.error);
  if (r.ok) {
    if (d.declined === true) return { kind: 'declined', message, data: d as Partial<T> };
    return { kind: 'ok', data: d as unknown as T };
  }
  if (r.status === 402) return { kind: 'declined', message, data: d as Partial<T> };
  // Η υπογραφή του παρόχου έρχεται ΠΡΙΝ από το τερματικό: σίγουρα καμία χρέωση.
  if (r.status === 502 && code === 'signature_failed') return { kind: 'failed', message, code };
  if (r.status >= 500) return { kind: 'uncertain', message, code, id: num(d.payment_id) ?? num(d.refund_id) };
  return { kind: 'error', status: r.status, message, code };
}

/** Δίκτυο / timeout: το αίτημα ίσως έφτασε και το τερματικό ίσως χρέωσε. */
const lost = <T, >(e: unknown): ChargeOutcome<T> => ({
  kind: 'uncertain',
  message: null,
  code: (e as { name?: string })?.name === 'AbortError' ? 'timeout' : 'network',
  id: null,
});

async function charge<T>(path: string, body: unknown): Promise<ChargeOutcome<T>> {
  try {
    return toOutcome<T>(await send(path, { method: 'POST', body, timeoutMs: CHARGE_TIMEOUT_MS }));
  } catch (e) {
    return lost<T>(e);
  }
}

export interface ListQuery {
  reservationId?: number;
  customerId?: number;
  phone?: string;
  status?: 'open' | 'all';
  limit?: number;
}

const qs = (q: Record<string, string | number | undefined | null>) => {
  const parts = Object.keys(q)
    .filter(k => q[k] != null && q[k] !== '')
    .map(k => `${k}=${encodeURIComponent(String(q[k]))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

export const depositsApi = {
  config: (storeId: number) => call<DepositsConfig>(`/stores/${storeId}/deposits/config`),
  list: (storeId: number, q: ListQuery = {}) =>
    call<{ deposits: DepositView[] }>(`/stores/${storeId}/deposits${qs({ ...q })}`).then(r => r.deposits || []),
  get: (storeId: number, id: number) => call<{ payment: DepositView; refunds: RefundView[] }>(`/stores/${storeId}/deposits/${id}`),
  create: (storeId: number, body: CreateDepositBody) => charge<CreateDepositResponse>(`/stores/${storeId}/deposits`, body),
  refund: (storeId: number, id: number, body: RefundBody) => charge<RefundResponse>(`/stores/${storeId}/deposits/${id}/refund`, body),
  /** Τα ενεργά τερματικά του καταστήματος (όπως στο DineInOperations). */
  terminals: async (storeId: number): Promise<PosTerminalLite[]> => {
    const data = await call<unknown>(`/stores/${storeId}/pos/terminals`);
    return (Array.isArray(data) ? data : []).filter((t: PosTerminalLite) => t && t.is_active);
  },
  /** Ο διακόπτης του καταστήματος → το νέο config (403 permission_denied, 409 counter_deposits_not_ready). */
  saveSettings: (storeId: number, body: DepositsSettingsBody) =>
    call<DepositsConfig>(`/stores/${storeId}/deposits/settings`, { method: 'PUT', body }),
  /**
   * Οι ανοιχτές προκαταβολές ενός πελάτη για την πληρωμή του Take Away — με ΟΛΟ το τηλέφωνο (≥ 10 ψηφία),
   * ποτέ με κομμάτι (400 phone_required, 403 feature_disabled, 409 counter_deposits_not_ready).
   */
  customerDeposits: (storeId: number, phone: string) =>
    call<{ deposits: CustomerDeposit[] }>(`/stores/${storeId}/deposits/customer${qs({ phone: String(phone || '').replace(/\D/g, '') })}`)
      .then(r => (Array.isArray(r.deposits) ? r.deposits : [])),
};

/**
 * Κλειδί ΜΙΑΣ προσπάθειας (8–64 χαρακτήρες [A-Za-z0-9_-]): ο server επιστρέφει την ίδια προκαταβολή
 * για το ίδιο κλειδί και δεν χρεώνει ποτέ δεύτερη φορά.
 */
export function newClientKey(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID().replace(/[{}]/g, '');
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.padEnd(16, '0');
}
